import React from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  Button,
  SkeletonBodyText,
  SkeletonDisplayText,
  Divider,
} from '@shopify/polaris';
import {
  useWebhookSyncStatus,
  useEnableWebhookSync,
  useDisableWebhookSync,
} from '../hooks/useWebhookSync';
import { PageEntrance, StaggeredList } from '../components/ui';
import { formatDateTime } from '../utils/dateFormat';
import './WebhookSyncPage.css';

/**
 * Phase 3 — F3.3: Webhook Sync Settings Page.
 *
 * Allows merchants to enable/disable real-time product sync.
 * Shows sync status dashboard with event counts and last reconciliation.
 */
export function WebhookSyncPage(): React.ReactElement {
  const { data: status, isLoading, isError, error } = useWebhookSyncStatus();
  const enableSync = useEnableWebhookSync();
  const disableSync = useDisableWebhookSync();

  return (
    <PageEntrance>
    <Page
      title="Real-Time Sync"
      subtitle="Automatically rescore products when they change in Shopify"
      backAction={{ content: 'Dashboard', url: '/dashboard' }}
    >
      <BlockStack gap="400">
        {/* Hero CTA */}
        <div className="z-hero">
          <div className="z-hero-content">
            <h2 className="z-hero-title">🔄 Keep scores fresh — automatically</h2>
            <p className="z-hero-subtitle">
              Enable real-time sync so product changes in Shopify instantly trigger rescoring and feed regeneration.
            </p>
          </div>
        </div>
      <Layout>
        {isError && (
          <Layout.Section>
            <Banner tone="critical">
              {error?.message ?? 'Failed to load sync status.'}
            </Banner>
          </Layout.Section>
        )}

        {/* Enable/Disable Toggle */}
        <Layout.Section>
          {isLoading ? (
            <Card>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={4} />
            </Card>
          ) : (
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd">
                      Webhook-Driven Sync
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      When enabled, products are automatically rescored within
                      minutes of being updated in Shopify. Your feed is also
                      regenerated to reflect changes.
                    </Text>
                  </BlockStack>
                  <Badge tone={status?.enabled ? 'success' : undefined} size="large">
                    {status?.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </InlineStack>

                <InlineStack gap="300">
                  {status?.enabled ? (
                    <Button
                      onClick={() => disableSync.mutate()}
                      loading={disableSync.isPending}
                      tone="critical"
                    >
                      Disable Sync
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={() => enableSync.mutate()}
                      loading={enableSync.isPending}
                    >
                      Enable Sync
                    </Button>
                  )}
                </InlineStack>

                {enableSync.isError && (
                  <Banner tone="critical">
                    {enableSync.error.message}
                  </Banner>
                )}
                {disableSync.isError && (
                  <Banner tone="critical">
                    {disableSync.error.message}
                  </Banner>
                )}
              </BlockStack>
            </Card>
          )}
        </Layout.Section>

        {/* Status Dashboard */}
        {status?.enabled && (
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Sync Dashboard
                </Text>
                <InlineStack gap="600">
                  <BlockStack gap="100" align="center">
                    <Text as="span" variant="heading2xl">
                      {status.processedToday}
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Processed Today
                    </Text>
                  </BlockStack>
                  <BlockStack gap="100" align="center">
                    <Text as="span" variant="heading2xl">
                      {status.pendingEvents}
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Pending
                    </Text>
                  </BlockStack>
                  <BlockStack gap="100" align="center">
                    <Text
                      as="span"
                      variant="heading2xl"
                      tone={status.failedToday > 0 ? 'critical' : undefined}
                    >
                      {status.failedToday}
                    </Text>
                    <Text as="span" variant="bodySm" tone="subdued">
                      Failed Today
                    </Text>
                  </BlockStack>
                </InlineStack>
                <Divider />
                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm" tone="subdued">
                    Last Reconciliation
                  </Text>
                  <Text as="span" variant="bodySm">
                    {status.lastReconciliationAt
                      ? formatDateTime(status.lastReconciliationAt)
                      : 'Never'}
                  </Text>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>
        )}

        {/* How It Works */}
        <Layout.Section variant="oneThird">
          <StaggeredList>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  How It Works
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  When you update a product in Shopify, Zelium receives a webhook
                  notification. The product is automatically rescored and your
                  feed is regenerated within the hour.
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Debounce Protection
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  If the same product is updated multiple times within a minute,
                  only the last update is processed. This prevents unnecessary
                  rescoring during bulk edits.
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">
                  Daily Reconciliation
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  A daily job checks for any missed webhooks and re-queues stale
                  products. This ensures your data stays fresh even if a webhook
                  is lost.
                </Text>
              </BlockStack>
            </Card>
          </StaggeredList>
        </Layout.Section>
      </Layout>
      </BlockStack>
    </Page>
    </PageEntrance>
  );
}
